/* eslint-disable indent */
import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import './AppDeclined.css';
import { LinkWrapper } from '../LinkWrapper/LinkWrapper';
import { renderToString } from 'react-dom/server';
import { acquisitionApiRequest } from '../../utils/acquisition-api/AcquisitionApi';

import axios from 'axios';
import { flowDataModule } from '../../utils/flowDataModule';
import { AcquisitionsLoader } from '../AcquisitionsLoader/AcquisitionsLoader';
import { getParameterByName, safeParse } from '../../utils/cob-utils';
import { AcquisitionFlow, getAcquisitionFlow } from '../../utils/acquisition-utils';
import SessionStorage from '../../utils/SessionStorage';
import { Section, Row, Col, ScrollableContainer } from '../../importCL';


interface IDeclineApiResponseData {
    BureauFactorReasons: Array<string>;
    BureauFactorCodes: Array<string>;
    BureauFactorReasonsWithOutFifthFactor: Array<string>;
    City: string;
    CreditBureauContactInformation: string;
    CreditScore: number;
    CreditScoreDate: string;
    CreditScoreHighRange: number;
    CreditScoreInfoOpeningText: string;
    CreditScoreLowRange: number;
    CustomerAddressLine1: string;
    CustomerAddressLine2: string;
    CustomerName: string;
    InquiryCode: string;
    LetterNumber: string;
    NcdDriverLicenseVariables: Array<string>;
    NonCreditDeclineReason: string;
    State: string;
    ZipCode: string;
    IsDecisionFromCrsScore: boolean;
    AdditionalInformation: {
        FirstName: string;
        ZipCode: string;
        DeclineReason: string;
        ProgramCodeDeclinedFor: string;
        CreditScore: number;
    };
}
interface IDynamicDeclineExperienceData {
    disclaimer?: string;
    title?: string;
    mobileTitle?: string;
    mobileSmallTitle?: string;
    subtitle?: string;
    mobileSubTitle?: string;
    referralTitle?: string;
    placeReferralsOnBottom?: boolean;
    smallTitle?: string;
    secondReferralImage?: string;
    secondReferralImageAdvText?: string;
    secondReferralImageAltText?: string;
    secondReferralImageCTAText?: string;
    secondReferralImageSubText?: string;
    secondReferralCTALink?: string;
    secondReferralOpenInNewTab?: string;
    singleReferralImage?: string;
    singleReferralImageAdvText?: string;
    singleReferralImageAltText?: string;
    singleReferralImageCTAText?: string;
    singleReferralCTALink?: string;
    singleReferralImageSubText?: string;
    singleReferralImageOnly?: boolean;
    singleReferralOpenInNewTab?: boolean;
    instructions?: string;
    logo?: string;
    videoAltText?: string;
    videoLink?: string;
    videoDescription?: string;
    linkToPPQWidget?: string;
}

export const AppDeclinedEditConfig = {
    emptyLabel: 'App Declined',
    isEmpty: (props: PropsWithChildren<IDynamicDeclineExperienceData>) => {
        return !props;
    },
};

const getFormattedDate = (dateStr?: any) => {
    const date = dateStr ? new Date(dateStr as string) : new Date();

    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return month + '/' + day + '/' + year;
};

const getDataAsList = (list: any) => {
    if (!list || !list.length) {
        return '';
    }

    // handle PHA lists represented as string
    if (typeof list === 'string') {
        list = safeParse(list);
    }

    return renderToString(
        <ul className='ApplicationStatusForm__listContainer'>
            {list.map((item: string, index: number) => (
                <li key={index}>{item}</li>
            ))}
        </ul>,
    );
};

const AppDeclined = ({ noDeclineLetterRedirect }: PropsWithChildren<{
    noDeclineLetterRedirect?: string;
}>): JSX.Element => {
    const [isSpinning, setSpinning] = useState(true);
    const [letter, setLetter] = useState('');
    const [dynamicDeclineData, setDynamicDeclineData] = useState<IDynamicDeclineExperienceData|null>(null);

    const fullAppResponseRef = useRef(null);
    const isPreQualFlow = getAcquisitionFlow() === AcquisitionFlow.PQ;
    const isSkipPreQualFlow =  getAcquisitionFlow() === AcquisitionFlow.SkipPQ;
    const isPqOfOneFlow = getAcquisitionFlow() === AcquisitionFlow.PqOfOne;
    const isPhaFlow = getAcquisitionFlow() === AcquisitionFlow.PHA;
    const flowApplicationData = SessionStorage.tryGetObject('flowApplicationData', null);

    const populateAppLetter = async (apiDeclineData?: any, contentData?: any) => {
        if (!apiDeclineData || !contentData) {
            return;
        }

        const appLetter = contentData.declineLetterContent;
        const processedAppLetter = await processAppLetter(appLetter, apiDeclineData);
        setLetter(processedAppLetter);
    };

    const processAppLetter = async (appLetter: any, data: any) => {
        if (!appLetter || !data) {
            return '';
        }

        const acquisitionFlow = getAcquisitionFlow();
        if(!SessionStorage.verify('ReservationNumber') && acquisitionFlow === AcquisitionFlow.PQ) {
            SessionStorage.set('ReservationNumber', data.InquiryCode);
        }

        if (window.location.href.indexOf('?') > -1) {
            await SessionStorage.setCipher('preQualData', JSON.stringify(data));
        }

        for (const [key, value] of Object.entries(data)) {
            // skip, this mapping requires unique handling
            if (key === 'CustomerAddressLine2') {
                continue;
            }

            const regx = new RegExp('{' + key + '}', 'g');
            if (Array.isArray(value)) {
                appLetter = appLetter.replace(regx, getDataAsList(value));
                continue;
            } 

            if (key.toLowerCase().includes('date')) {
                appLetter = appLetter.replace(regx, getFormattedDate(value as Date));
            }

            appLetter = appLetter.replace(regx, value as string);
        }

        appLetter = appLetter.replace(
                /{CustomerAddressLine2}/g,
                data.CustomerAddressLine2 && data.CustomerAddressLine2.length
                    ? '<br>' + data.CustomerAddressLine2
                    : data.CustomerAddressLine2,
            )
            .replace(/{CurrentDate}/g, getFormattedDate());
            
        return appLetter;
    };

    const declineApiCall = async (redirectedFromEsignPrequal: boolean) => {
        // default to PA
        if (isPhaFlow && flowApplicationData) {
            return undefined;
        }
        let declineApiUrl: string = 'preapproved-appdeclined';

        if (isPreQualFlow) {
            declineApiUrl = redirectedFromEsignPrequal
            ? 'prequal-declined'
            : 'prequal-appdeclined';
        } else if (isSkipPreQualFlow || isPqOfOneFlow) {
            declineApiUrl = 'skipprequal-appdeclined';
        }

        try {
            const result = await acquisitionApiRequest<IDeclineApiResponseData>(
                declineApiUrl,
                { method: 'GET' },
            );
            return result.data;
        } catch (err) {
            console.error('Decline Api Failed...');
        }

        return undefined;
    };

    /* eslint-disable @typescript-eslint/no-unused-vars */
    const broadcastToOtherTab = async () => {
        const flow = getAcquisitionFlow();
        let personalInfoBrowserChannel = {};
        if (flow === AcquisitionFlow.PQ ) {
            personalInfoBrowserChannel = await SessionStorage.tryGetCipher('PersonalInfoBrowserChannel', undefined);
            if (!personalInfoBrowserChannel) {
                return;
            }
        } else if (flow === AcquisitionFlow.SkipPQ) {
            const { PersonalInformation, ...rest } = await SessionStorage.tryGetCipher('prequalFullAppData', undefined);
            const { Ssn, fpBB, ioBlackBox, DOBDay, DOBMonth, DOBYear, ...filteredPersonalInfo } = PersonalInformation;
            personalInfoBrowserChannel = filteredPersonalInfo;
        } else if (flow === AcquisitionFlow.PA) {
            const { Applicant, ...rest } = await SessionStorage.tryGetCipher('preApprovalFullAppData', undefined);
            personalInfoBrowserChannel = {
                EmailAddress: Applicant.EmailAddress.Value,
                FirstName: Applicant.ApplicantName.FirstName,
                HomeAddressLine1: Applicant.HomeAddress.AddressLineOne,
                HomeAddressLine2: Applicant.HomeAddress.AddressLineTwo,
                HomeCity: Applicant.HomeAddress.City,
                HomeState: Applicant.HomeAddress.State,
                HomeZipCode: Applicant.HomeAddress.ZipCode,     
                LastName: Applicant.ApplicantName.LastName,
                MailingAddressLine1: Applicant.MailingAddress.AddressLineOne,
                MailingAddressLine2: Applicant.MailingAddress.AddressLineTwo,
                MailingCity: Applicant.MailingAddress.City,
                MailingState: Applicant.MailingAddress.State,
                MailingZipCode: Applicant.MailingAddress.ZipCode,
                MiddleInitial: Applicant.ApplicantName.MiddleInitial,
                NameSuffix: Applicant.ApplicantName.NameSuffix,
                
            };
        }

        if (Object.keys(personalInfoBrowserChannel).length < 1) {
            return;
        }
        const appDeclineBroadcastChannel = new BroadcastChannel('PQ_BroadcastChannel');
        appDeclineBroadcastChannel.onmessage = (e) => {
            if (e.data === 'Ready.') {
                appDeclineBroadcastChannel.postMessage(personalInfoBrowserChannel);
            }
        };
    };

    const getDeclineLetter = async (declineApiData?: IDeclineApiResponseData) => {
        // For PHA flow, get letter type code from flowApplicationData
        const letterNumber = declineApiData?.LetterNumber;

        if (!letterNumber) {
            // eslint-disable-next-line no-console
            console.debug('LetterNumber from Decline API does not exist');
            return;
        }

        try {
            const result = await axios.get(`/services/declineletter?declineLetterNumber=${letterNumber}`);
            if (!result?.data || !result?.data?.declineLetterContent) {
                console.error('Error getting data from content fragment...');
                return undefined;
            }
            return result.data;
        } catch (err) {
            console.error('Decline letter servlet failed....');
            if (noDeclineLetterRedirect) {
                window.location.assign(noDeclineLetterRedirect+window.location.search);
            }
        }

        return undefined;
    };

    const getDynamicDeclineExperience = async (declineApiData?: IDeclineApiResponseData, signal?: AbortSignal) => {
        const sourceID = getParameterByName('C1BSourceID') ?? SessionStorage?.tryGetObject('MarketingAttributes', null)?.C1BSourceID;

        // Use appropriate programCode based on flow
        let programCode;
        let declineReason;

        if ((isPhaFlow && flowApplicationData) || (isPreQualFlow && flowApplicationData)) {
            programCode = flowApplicationData?.card?.programCode;
            
            const fundingResponse = SessionStorage.tryGetObject('fundingResponse', {});
            declineReason = fundingResponse?.applicationStatus?.statusDescription || flowApplicationData.applicationStatus.statusDescription;
        } else {
            programCode = SessionStorage.get('selectedProgramCode');
            declineReason = declineApiData?.AdditionalInformation?.DeclineReason;
        }

        const servletParams = new URLSearchParams();

        if (sourceID) {
            servletParams.append('sourceId', sourceID);
        }

        if (programCode) {
            servletParams.append('programCode', programCode);
        }

        if (declineReason) {
            servletParams.append('declineReason', declineReason);
        }

        // Dev Note: This servlet is not expected to ever be unavailable. DX-11825
        // if no params then get default experience
        const servletUrl = `/services/dynamicDeclineExperience${servletParams.toString() ? '?' + servletParams.toString() : ''}`;
        axios.get(servletUrl, { signal: signal } ).then((response) => {
            setDynamicDeclineData({ ...response.data });
        // eslint-disable-next-line no-console
        }).catch(() => console.debug('Failed to retrieve dynamic decline experience content...'));
    };

    useEffect(() => {
        const abortController = new AbortController();
        const signal = abortController.signal;

        const initialize = async () => {
            const response = await SessionStorage.tryGetCipher('fullAppResponse', null);
            fullAppResponseRef.current = response;

            const redirectedFromEsignPrequal = !fullAppResponseRef.current || !fullAppResponseRef.current?.SubmitApplicationEnum;

            let declineApiResult;

            if ((isPhaFlow && flowApplicationData) || (isPreQualFlow && flowApplicationData)) {
                const fundingResponse = SessionStorage.getObject('fundingResponse');
                const mailingAddress = flowApplicationData.parties[0].addresses.find(addr => addr.usageType === 'MAILING');
                const declineData = {
                    // Map the flowApplicationData to the structure expected by the letter formatter
                    CustomerName: `${flowApplicationData.parties[0].prefix || ''} ${flowApplicationData.parties[0].firstName} ${flowApplicationData.parties[0].middleInitial || ''} ${flowApplicationData.parties[0].lastName} ${flowApplicationData.parties[0].suffix || ''}`.trim().replace(/\s+/g, ' '),
                    CustomerAddressLine1: mailingAddress?.addressLine1 || '',
                    CustomerAddressLine2: mailingAddress?.addressLine2 || '',
                    City: mailingAddress?.city || '',
                    State: mailingAddress?.state || '',
                    ZipCode: mailingAddress?.zipCode || '',
                    // decline should pull from funding response only.
                    LetterNumber: fundingResponse?.letter?.templateCode,
                    AdditionalInformation: {
                        FirstName: flowApplicationData.parties[0].firstName,
                        ZipCode: mailingAddress.zipCode,
                        DeclineReason: flowApplicationData.applicationStatus.applicationStatus,
                        CreditScore: flowApplicationData?.letter?.content?.variables?.find((v) => v.key === 'CreditScore')?.value || flowApplicationData.parties[0].creditProfiles?.[0]?.score,
                    },
                };

                // decline should pull from funding response only.
                const letterContent = fundingResponse?.letter?.content;

                letterContent?.variables?.forEach((variable) => {
                    Object.assign(declineData, { [variable.key as string]: variable?.value });
                });

                letterContent?.listVariables?.forEach((variable) => {
                    Object.assign(declineData, { [variable.key as string]: variable?.values });
                });

                await getDynamicDeclineExperience(declineData as IDeclineApiResponseData, signal);
                const declineLetterResult = await getDeclineLetter(declineData as IDeclineApiResponseData);

                populateAppLetter(declineData, declineLetterResult);
                await flowDataModule.updateInSession(declineData.AdditionalInformation);
            } else if (isPreQualFlow && SessionStorage.verify('fundingResponse')) {
                const prequalFullAppData = await SessionStorage.getCipher('prequalFullAppData', true);
                const fundingResponse = SessionStorage.getObject('fundingResponse');
                const declineLetterData = {
                    // Map the flowApplicationData to the structure expected by the letter formatter
                    CustomerName: `${prequalFullAppData?.PersonalInformation?.Prefix || ''} ${prequalFullAppData?.PersonalInformation?.FirstName} ${prequalFullAppData?.PersonalInformation?.MiddleInitial || ''} ${prequalFullAppData?.PersonalInformation?.LastName} ${prequalFullAppData?.PersonalInformation?.Suffix || ''}`.trim().replace(/\s+/g, ' '),
                    CustomerAddressLine1: prequalFullAppData?.PersonalInformation?.MailingAddressLine1 || '',
                    CustomerAddressLine2: prequalFullAppData?.PersonalInformation?.MailingAddressLine2 || '',
                    City: prequalFullAppData?.PersonalInformation?.MailingCity || '',
                    State: prequalFullAppData?.PersonalInformation?.MailingState || '',
                    ZipCode: prequalFullAppData?.PersonalInformation?.MailingZipCode || '',
                    LetterNumber: fundingResponse?.letter?.templateCode,
                    AdditionalInformation: {
                        FirstName: prequalFullAppData?.PersonalInformation?.FirstName,
                        ZipCode: prequalFullAppData?.PersonalInformation?.MailingZipCode || '',
                        DeclineReason: fundingResponse.applicationStatus.applicationStatus,
                        ProgramCodeDeclinedFor: SessionStorage.tryGet('selectedProgramCode', ''),
                        CreditScore: fundingResponse?.letter?.content?.variables?.find((v) => v.key === 'CreditScore')?.value || '',
                    },
                };

                fundingResponse?.letter?.content?.variables?.forEach((variable) => {
                    Object.assign(declineLetterData, { [variable.key as string]: variable?.value });
                });

                fundingResponse?.letter?.content?.listVariables?.forEach((variable) => {
                    Object.assign(declineLetterData, { [variable.key as string]: variable?.values });
                });

                await getDynamicDeclineExperience(declineLetterData as IDeclineApiResponseData, signal); 
                const declineLetterResult = await getDeclineLetter(declineLetterData as IDeclineApiResponseData);

                populateAppLetter(declineLetterData, declineLetterResult);
            } else {
                declineApiResult = await declineApiCall(redirectedFromEsignPrequal);
                await getDynamicDeclineExperience(declineApiResult, signal);
                const declineLetterResult = await getDeclineLetter(declineApiResult);

                populateAppLetter(declineApiResult, declineLetterResult);
                await flowDataModule.updateInSession(declineApiResult?.AdditionalInformation);
            }
        };

        initialize();
        return () => {
            abortController.abort();
        };
    }, []);

    // Note: We MUST show a letter.
    // only remove spinner if we have dynamic decline experience (or default) and the letter
    useEffect(() => {
        const updateAnalytics = () => {
            const reservationNumber = sessionStorage.getItem('ReservationNumber');
            const applicationId = sessionStorage.getItem('ApplicationId');
            const isPq = reservationNumber && reservationNumber.substring(0, 2) === 'PQ';
            return {
                programcode: sessionStorage.getItem('selectedProgramCode'),
                prequalificationReservationNo: isPq ? reservationNumber : null,
                prequalificationApplicationId: isPq ? applicationId : null,
                fullappapplicationReservationNo: !isPq ? reservationNumber : null,
                fullappapplicationID: !isPq ? applicationId : null,
                flowData: null,
            };
        }
        if (letter && dynamicDeclineData) {
            setSpinning(false);

            if (window.location.href.includes('pre-qualification/pre-qualification-result/pre-qual-decline')) {
                window.digitalData.analyticsData = updateAnalytics();
            }
        }
        // else, spinner stays on because we have no way of handling if the decline api fails at this point
    }, [letter, dynamicDeclineData]);

    useEffect(() => {
        if (!isSpinning) {
            window.dispatchEvent(new Event('resize'));
        }
    }, [isSpinning]);

    return (
        <>
            {isSpinning ? (<AcquisitionsLoader processingSteps={[]} />)
                :
                (<>
                <Section id='app-declined' className='AppDeclined py-8'>
                    <div className='py-0'>
                        <Row>
                            <Col>
                                <div className='mb-8 AppDeclined__disclaimer'>
                                    {dynamicDeclineData?.disclaimer &&
                                        transformHTMLParserLinks(dynamicDeclineData?.disclaimer)}
                                </div>
                                {dynamicDeclineData?.title && (
                                    <h3
                                        className={`AppDeclined__small--title ${
                                            dynamicDeclineData.linkToPPQWidget
                                                ? 'AppDeclined__desktopAndMobile'
                                                : 'AppDeclined__desktop--only'
                                        }`}
                                    >
                                        {transformHTMLParserLinks(dynamicDeclineData?.title)}
                                        {dynamicDeclineData?.smallTitle && (
                                            <span className='AppDeclined__title--small'>
                                                {transformHTMLParserLinks(
                                                    dynamicDeclineData?.smallTitle,
                                                )}
                                            </span>
                                        )}
                                    </h3>
                                )}
                                {dynamicDeclineData?.mobileTitle && (
                                    <h3 className='AppDeclined__small--title AppDeclined__mobile--only'>
                                        {transformHTMLParserLinks(dynamicDeclineData?.mobileTitle)}
                                        {dynamicDeclineData?.mobileSmallTitle && (
                                            <span className='AppDeclined__title--small'>
                                                {transformHTMLParserLinks(
                                                    dynamicDeclineData?.mobileSmallTitle,
                                                )}
                                            </span>
                                        )}
                                    </h3>
                                )}
                                {dynamicDeclineData?.subtitle && (
                                    <h4
                                        className={`AppDeclined__subTitle ${dynamicDeclineData.linkToPPQWidget ? 'AppDeclined__desktopAndMobile' : 'AppDeclined__desktop--only'}`}>
                                        {transformHTMLParserLinks(dynamicDeclineData?.subtitle)}
                                    </h4>
                                )}
                                {dynamicDeclineData?.mobileSubTitle && (
                                    <h4 className='AppDeclined__subTitle AppDeclined__mobile--only'>
                                        {transformHTMLParserLinks(
                                            dynamicDeclineData?.mobileSubTitle,
                                        )}
                                    </h4>
                                )}
                            </Col>
                        </Row>
                        <Row
                            className={`AppDeclined__columns
                            ${
                                dynamicDeclineData?.placeReferralsOnBottom
                                    ? 'AppDeclined__featured--offers'
                                    : ''}
                        `}>
                            {!dynamicDeclineData?.linkToPPQWidget && (<Col lg={6} xs={12} md={12}>
                                <div className='AppDeclined__refTitle'>
                                    {dynamicDeclineData?.referralTitle && (
                                        <h3 className='AppDeclined__ref--title'>
                                            {transformHTMLParserLinks(dynamicDeclineData?.referralTitle)}
                                        </h3>
                                    )}
                                    {!(dynamicDeclineData?.referralTitle) && (
                                        <div className='AppDeclined__refTitle--dynamicSpacing'></div>
                                    )}
                                    {dynamicDeclineData?.videoDescription && (
                                        <h4 className='AppDeclined__videoDescription'>
                                            {transformHTMLParserLinks(dynamicDeclineData?.videoDescription)}
                                        </h4>
                                    )}
                                    {(dynamicDeclineData?.singleReferralImage || dynamicDeclineData?.secondReferralImage) &&
                                    !dynamicDeclineData?.singleReferralImageOnly && (
                                        <ul className='AppDeclined__preQualReferrals'>
                                            {dynamicDeclineData?.singleReferralImage && (
                                                <li
                                                    className='AppDeclined__singleRefLink'
                                                    data-testid='test-singleRefImg'
                                                >
                                                    <LinkWrapper
                                                        href={dynamicDeclineData?.singleReferralCTALink}
                                                        target={
                                                            dynamicDeclineData?.singleReferralOpenInNewTab ? '_blank' : '_self'
                                                        }
                                                    >
                                                        {dynamicDeclineData?.singleReferralImage && (
                                                            <img
                                                                loading='lazy'
                                                                src={
                                                                    dynamicDeclineData?.singleReferralImage
                                                                }
                                                                alt={
                                                                    dynamicDeclineData?.singleReferralImageAltText
                                                                }
                                                            />
                                                        )}
                                                    </LinkWrapper>
                                                </li>
                                            )}
                                            {dynamicDeclineData?.secondReferralImage && (
                                                <li data-testid='test-secondRefImg'>
                                                    <LinkWrapper
                                                        href={dynamicDeclineData?.secondReferralCTALink}
                                                        target={
                                                            dynamicDeclineData?.secondReferralOpenInNewTab ? '_blank' : '_self'
                                                        }
                                                    >
                                                        <img 
                                                            loading='lazy'
                                                            src={dynamicDeclineData?.secondReferralImage}
                                                            alt={
                                                                dynamicDeclineData?.secondReferralImageAltText
                                                            }
                                                        />
                                                    </LinkWrapper>
                                                </li>
                                            )}
                                        </ul>
                                    )}
                                </div>
                                <div>
                                    {dynamicDeclineData?.videoLink && (
                                        <iframe className='AppDeclined__videoLink'
                                            src={dynamicDeclineData?.videoLink}
                                            allow='fullscreen'></iframe>
                                    )}
                                </div>
                                <div>
                                    {dynamicDeclineData?.singleReferralImage && dynamicDeclineData?.singleReferralImageOnly && (
                                        <ul
                                            data-testid='test-singleRefImg'
                                            className='AppDeclined__pre__qual__referrals'
                                        >
                                            <li className='AppDeclined__singleRefLink'>
                                                <LinkWrapper
                                                    data-testid='declined-ad'
                                                    href={dynamicDeclineData?.singleReferralCTALink ?? ''}
                                                    target={
                                                        dynamicDeclineData?.singleReferralOpenInNewTab ? '_blank' : '_self'
                                                    }
                                                    onClick={ () => broadcastToOtherTab() }
                                                >
                                                    <img
                                                        loading='lazy'
                                                        src={dynamicDeclineData?.singleReferralImage}
                                                        alt={
                                                            dynamicDeclineData?.singleReferralImageAltText
                                                        }
                                                    />
                                                </LinkWrapper>
                                            </li>
                                        </ul>
                                    )}
                                </div>
                            </Col>
                            )}
                            <Col
                                xs={12}
                                md={12}
                                lg={!dynamicDeclineData?.linkToPPQWidget && 6}
                                className='AppDeclined__declineCol'
                            >
                                <div className='AppDeclined__contentImageContainer'>
                                    <div
                                        className={
                                            dynamicDeclineData?.placeReferralsOnBottom
                                                ? 'decline-detailed-info'
                                                : ''
                                        }
                                    >
                                        {dynamicDeclineData?.instructions && (
                                            <div
                                                className={`AppDeclined__declinedScrollDown ${
                                                    dynamicDeclineData.linkToPPQWidget &&
                                                    'AppDeclined__declinedScrollDown--full'
                                                }`}
                                            >
                                                {transformHTMLParserLinks(
                                                    dynamicDeclineData?.instructions,
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <ScrollableContainer data-testid='test-letter'
                                     className={`AppDeclined__declineLetter ${dynamicDeclineData?.linkToPPQWidget
                                        ? 'pb-0'
                                        : ''}`} print addBorder>
                                      {transformHTMLParserLinks(letter.toString())}
                                </ScrollableContainer>
                            </Col>
                        </Row>
                    </div>
                </Section>
                {dynamicDeclineData?.linkToPPQWidget && (
                    <div className='AppDeclinedIframe'>
                        <iframe
                            src='https://www.getcreditone.com/widget/widget-ppq-form-name/?affiliates=false'
                            title='Embedded Content'
                            marginHeight={0}
                            marginWidth={0}
                            scrolling='no'
                            style={{ width: '100%', border: 'none' }}
                        ></iframe>
                    </div>
                )}
                </>
                )
            }
            
        </>
    );
};

export default AppDeclined;
